import PptxGenJS from 'pptxgenjs';
import ISeason from '@app/types/data/base/ISeason';
import ISeasonWithPrice from '@app/types/data/base/ISeasonWithPrice';
import {
	ILabelPosition,
	ISeasonPosition,
	SeasonCalendarBaseClass
} from '@components/020-SeasonCalendar/SeasonCalendarBaseClass';
import { colors, elementSizes, presentationDimensions } from '@app/services/export/ppt/variables/constants';
import { copyMini } from '@app/services/export/ppt/variables/typography';
import SeasonType from '@app/types/base/SeasonType';

interface IProps {
	seasons: ISeason[] | ISeasonWithPrice[];
	startDateRange?: string;
	endDateRange?: string;
}

interface IPosition {
	x: number;
	w: number;
}

export class SeasonCalendar extends SeasonCalendarBaseClass {
	protected slide: PptxGenJS.Slide;

	protected props: IProps;

	protected position: IPosition;

	private readonly labelHeight = 0.15;

	private readonly yPos: number;

	protected labelsWithPosition: ILabelPosition[] = [];

	protected seasonsWithPosition: ISeasonPosition[] = [];

	public constructor(slide: PptxGenJS.Slide, props: IProps, position: IPosition) {
		super(props.seasons, props.startDateRange, props.endDateRange, { locale: 'de-DE' });
		this.slide = slide;
		this.props = props;
		this.position = position;
		this.yPos = presentationDimensions.h - elementSizes.footnoteHeight - elementSizes.seasonCalendarHeight - 0.1;
	}

	public create(): void {
		this.labelsWithPosition.push(...this.getLabelsWithPosition());
		this.seasonsWithPosition.push(...this.getSeasonsWithPosition());

		this.placeSeasonBars();
		this.placeMonthLabels();
	}

	private placeMonthLabels(): void {
		const y = this.yPos + (elementSizes.seasonCalendarHeight - this.labelHeight);

		this.labelsWithPosition.forEach((item, index) => {
			const parentWidthInPixel = this.position.w;
			const childWidthInPercent = item.widthPercent;
			const childOffsetInPercent = item.leftPercent;
			const w = (childWidthInPercent / 100) * parentWidthInPixel;
			const x = this.position.x + (childOffsetInPercent / 100) * parentWidthInPixel;

			this.slide.addText(item.label, {
				...copyMini,
				x,
				y,
				w,
				h: this.labelHeight,
				valign: 'middle',
				align: 'center',
				color: colors.blue,
				margin: 0
			});

			// divider
			if (index > 0) {
				this.slide.addShape('line', {
					line: {
						color: colors.white,
						width: 1
					},
					y: this.yPos,
					x,
					w: 0,
					h: elementSizes.seasonCalendarHeight - this.labelHeight
				});
			}
		});
	}

	private placeSeasonBars(): void {
		// sort seasons with type "Default Season" first
		this.seasonsWithPosition.sort((a, b) => {
			if (a.type === SeasonType.DefaultSeason && b.type !== SeasonType.DefaultSeason) return -1;
			if (a.type !== SeasonType.DefaultSeason && b.type === SeasonType.DefaultSeason) return 1;
			return 0;
		});

		// background
		this.slide.addShape('rect', {
			y: this.yPos,
			x: this.position.x,
			w: this.position.w,
			h: elementSizes.seasonCalendarHeight - this.labelHeight,
			fill: { color: colors.lightGray }
		});

		this.seasonsWithPosition.forEach(item => {
			let color = colors.defaultSeason;
			const parentWidthInPixel = this.position.w;
			const childWidthInPercent = item.widthPercent;
			const childOffsetInPercent = item.leftPercent;
			const w = (childWidthInPercent / 100) * parentWidthInPixel;
			const x = this.position.x + (childOffsetInPercent / 100) * parentWidthInPixel;

			if (item.type === SeasonType.LowSeason) color = colors.lowSeason;
			if (item.type === SeasonType.HighSeason) color = colors.highSeason;

			this.slide.addShape('rect', {
				y: this.yPos,
				x,
				w,
				h: elementSizes.seasonCalendarHeight - this.labelHeight,
				fill: { color }
			});
		});
	}
}
